import { defineStore } from 'pinia'
import { createEntityCache, pruneIds } from '~/stores/entityCache'
import { useWebChatStore } from '~/stores/web-chat'
import { planAllows } from '~/composables/usePlanFeature'
import type { Client, ClientParty } from '~~/shared/clients/client'
import type { PersoneriaRelationship } from '~~/shared/clients/personeria'
import type {
  ClientEntity, ClientEntityAttribute, ClientEntityAttributeCandidate, ClientDocument,
} from '~~/shared/clients/clientEntity'
import type { ClientDocumentLink } from '~~/shared/clients/clientDocument'
import type { BulkDeleteResult, SuccessResult } from '~~/shared/apiResults'

/**
 * THE clients store (MAR-125). Clients = the top-level "Clients / Projects" entity, org-scoped.
 * See https://app.notion.com/p/3c304e9a592881b796eddb0748c5870a.
 *
 * Replaces `composables/useClients.ts` and `composables/useClientEntity.ts`, which were **stateless
 * API wrappers**: they held no refs at all, so every page that showed a client kept its own `ref` and
 * refetched. Nothing shared, nothing invalidated — renaming a client updated the page you did it on
 * and left every other surface showing the old name until reload.
 *
 * The endpoints moved here unchanged; what is new is that their responses land in one cache.
 *
 * **Clients reference cases and documents by id, never by copy** — the same rule the documents store
 * follows. A client's documents come from `/api/clients/:id/documents`, a projection that carries
 * case context; those rows are a VIEW belonging to the ficha, not a second document store.
 */

// Re-exported: these names are imported from the store across the app. The DECLARATIONS live in
// `shared/clients/**`, which is what the services can be annotated with.
export type {
  Client, ClientParty,
  ClientEntity, ClientEntityAttribute, ClientEntityAttributeCandidate, ClientDocument,
}

/** `PERSONERIA_RELATIONSHIPS` (`shared/clients/personeria.ts`) — one vocabulary, one home (MAR-975). */
export type PartyRelationship = PersoneriaRelationship

export interface PartyLinkInput {
  idType: string
  idNumber: string
  name?: string
  kind?: 'person' | 'organization' | null
  relationship?: PartyRelationship
}

export interface CreateClientInput {
  name: string
  description?: string
  defaultAiInstructions?: string
  statusNote?: string
  parties?: PartyLinkInput[]
}

// ── Canonical-entity reads for the client ficha (C3) ──
// The single client-side seam onto the canonical-entity model. See
// https://app.notion.com/p/3c304e9a592881319997c862288b27dc.
// The shapes are declared in `shared/clients/clientEntity.ts` and re-exported above.

export const useClientsStore = defineStore('clients', () => {
  const { apiFetch } = useApi()

  const cache = createEntityCache<Client>()
  /** The org's clients, in server order — the view behind `/app/clients`. */
  const listIds = ref<string[]>([])
  const loading = ref(false)
  const loaded = ref(false)

  const list = computed<Client[]>(() => cache.resolve(listIds.value))

  function get(id: string): Client | null {
    return cache.get(id)
  }

  // ── Reads ─────────────────────────────────────────────────────────────────────────────────────
  /**
   * Fetch the client list into the cache and return it.
   *
   * Returns the rows as well as caching them so the one-shot callers that predate this store keep
   * working unchanged; anything that wants to STAY current reads `list` instead.
   */
  async function listClients(q?: string): Promise<Client[]> {
    // Clients ride the `case_management` gate with cases — see `listCases` for why this never asks.
    if (!(await planAllows('case_management'))) {
      if (!q) {
        listIds.value = []
        loaded.value = true
      }
      return []
    }
    loading.value = true
    try {
      const rows = await apiFetch<Client[]>('/api/clients', { query: q ? { q } : undefined })
      cache.upsert(rows)
      // A filtered search is not the canonical list — it must not replace the view behind /app/clients.
      if (!q) {
        listIds.value = rows.map(r => r.id)
        loaded.value = true
      }
      return rows
    } finally {
      loading.value = false
    }
  }

  async function getClient(id: string): Promise<Client> {
    const client = await apiFetch<Client>(`/api/clients/${id}`)
    cache.upsert([client])
    return client
  }

  // ── Mutations ─────────────────────────────────────────────────────────────────────────────────
  async function createClient(body: CreateClientInput): Promise<Client> {
    const client = await apiFetch<Client>('/api/clients', { method: 'POST', body })
    cache.upsert([client])
    if (!listIds.value.includes(client.id)) listIds.value = [client.id, ...listIds.value]
    return client
  }

  async function updateClient(
    id: string,
    body: Partial<CreateClientInput> & { status?: string },
  ): Promise<Client> {
    const client = await apiFetch<Client>(`/api/clients/${id}`, { method: 'PUT', body })
    cache.upsert([client])
    return client
  }

  /**
   * Upload a client's logo (MAR-130). Multipart, so the body is a FormData rather than JSON — the
   * fetch layer must not set a Content-Type here, or the multipart boundary is lost.
   *
   * The response is the updated client row, cached like any other write, so every surface showing
   * this client repaints at once instead of only the page that did the upload.
   */
  async function uploadClientLogo(id: string, file: File): Promise<Client> {
    const form = new FormData()
    form.append('file', file)
    const client = await apiFetch<Client>(`/api/clients/${id}/logo`, { method: 'POST', body: form })
    cache.upsert([client])
    return client
  }

  async function removeClientLogo(id: string): Promise<Client> {
    const client = await apiFetch<Client>(`/api/clients/${id}/logo`, { method: 'DELETE' })
    cache.upsert([client])
    return client
  }

  async function deleteClient(id: string) {
    const res = await apiFetch<SuccessResult>(`/api/clients/${id}`, { method: 'DELETE' })
    forget([id])
    await unscopeChat([id])
    return res
  }

  /** Atomic multi-delete — one statement server-side, so a partial batch can't happen. */
  async function deleteClients(ids: string[]) {
    const res = await apiFetch<BulkDeleteResult>(
      '/api/client-bulk-delete', { method: 'POST', body: { ids } },
    )
    const deleted = res.deletedIds ?? ids
    forget(deleted)
    await unscopeChat(deleted)
    return res
  }

  /**
   * Deleting a client the chat is scoped to clears the chip (MAR-461, `clearScopeForDeleted`).
   * Deleting the client of the CASE it is scoped to does not — `cases.client_id` is
   * `ON DELETE set null`, so that case survives as "Sin cliente" and only the rung's client half is
   * dropped. Hung off the delete calls rather than `forget`, which is also a plain cache eviction.
   */
  const unscopeChat = (ids: string[]) => useWebChatStore().clearScopeForDeleted(ids)

  function forget(ids: string[]) {
    cache.remove(ids)
    listIds.value = pruneIds(listIds.value, new Set(ids))
  }

  // ── Parties ───────────────────────────────────────────────────────────────────────────────────
  async function removeClientParty(id: string, dependencyId: string) {
    const res = await apiFetch<SuccessResult>(`/api/clients/${id}/parties/${dependencyId}`, { method: 'DELETE' })
    void getClient(id)
    return res
  }

  // ── Canonical entities (the ficha) ────────────────────────────────────────────────────────────
  // Pass-through reads: these are projections over the entity model, not client records, so they are
  // deliberately not cached here.
  const getClientEntities = (clientId: string) =>
    apiFetch<ClientEntity[]>(`/api/clients/${clientId}/entities`)

  const getClientDocuments = (clientId: string) =>
    apiFetch<ClientDocument[]>(`/api/clients/${clientId}/documents`)

  /**
   * Attach a document DIRECTLY to a client — `client_documents` (AlexAmin/marcus#83).
   *
   * Until #83 a document reached a client only through one of its cases, which made a whole real
   * state unrepresentable: the client-level papers a firm holds before any matter exists (escritura
   * de constitución, NIT, credenciales). Idempotent server-side, so re-attaching what is already
   * attached returns the existing link rather than failing.
   *
   * No `void getClient(id)` refresh, unlike the party calls above: the cached client row carries no
   * document count for this to invalidate. The rollup on the page is what re-reads, through
   * `getClientDocuments`.
   */
  const addClientDocument = (clientId: string, itemId: string) =>
    apiFetch<ClientDocumentLink>(`/api/clients/${clientId}/documents`, {
      method: 'POST',
      body: { itemId },
    })

  /** Detach a document from a client. Removes ONLY the direct edge — a case link is the case's. */
  const removeClientDocument = (clientId: string, itemId: string) =>
    apiFetch<SuccessResult>(`/api/clients/${clientId}/documents/${itemId}`, { method: 'DELETE' })

  /** Pick the canonical value for one attribute (the ficha dropdown). Values are never normalized. */
  const selectAttribute = (clientId: string, entityId: string, attributeId: string) =>
    apiFetch<SuccessResult>(`/api/clients/${clientId}/entities/${entityId}/select-attribute`, {
      method: 'POST',
      body: { attributeId },
    })

  return {
    byId: cache.byId, list, loading, loaded, get, forget,
    listClients, getClient, createClient, updateClient, deleteClient, deleteClients,
    uploadClientLogo, removeClientLogo,
    removeClientParty,
    getClientEntities, getClientDocuments, addClientDocument, removeClientDocument, selectAttribute,
  }
})
